/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * The inbox could not find out whether a mailbox is connected (network error,
 * 5xx). Previously this looked exactly like "not connected", which sent people
 * off to reconnect an account that was fine.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Box, Button, CircularProgress, Paper, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloudOffOutlinedIcon from '@mui/icons-material/CloudOffOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';

function InboxLoadError({ message, retrying, onRetry, onOpenSettings }) {
  return (
    <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
      <Paper elevation={0} sx={{ maxWidth: 420, width: '100%', p: { xs: 3, sm: 4 }, borderRadius: 3, border: 1, borderColor: 'divider', textAlign: 'center' }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            mx: 'auto',
            mb: 2,
            borderRadius: '50%',
            display: 'grid',
            placeItems: 'center',
            color: 'error.main',
            bgcolor: (theme) => alpha(theme.palette.error.main, 0.1),
          }}
        >
          <CloudOffOutlinedIcon />
        </Box>
        <Typography variant="h6" component="h2" gutterBottom>
          We couldn&apos;t load your mailbox
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Your email connection settings didn&apos;t load. This is usually a brief connection problem; your mailbox is still connected.
        </Typography>
        {message && (
          <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 2, overflowWrap: 'anywhere' }}>
            {message}
          </Typography>
        )}
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            onClick={onRetry}
            disabled={retrying}
            startIcon={retrying ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
          >
            {retrying ? 'Retrying...' : 'Try again'}
          </Button>
          <Button variant="text" onClick={onOpenSettings}>
            Email settings
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}

InboxLoadError.propTypes = {
  message: PropTypes.string,
  retrying: PropTypes.bool,
  onRetry: PropTypes.func.isRequired,
  onOpenSettings: PropTypes.func.isRequired,
};

InboxLoadError.defaultProps = { message: '', retrying: false };

export default InboxLoadError;
